import produce from 'immer';
import { isEmpty, uniq, upperCase } from 'lodash';
import { memo, useState } from 'react';
import { BsFillPlusCircleFill, BsPlusCircle } from 'react-icons/bs';

import { isNotEmpty } from '@cyren/common-lib';
import classNames from 'classnames';
import { MdClose } from 'react-icons/md';
import {
  getMainOptionAnswerKeys,
  getMutualExclusiveAnswerKeys,
  getRequireNoteAnswerKeys,
} from '../../../../utils/models/question';
import { useTranslations } from '../../../hooks/use-translations';
import { BodySelectorProps } from '../../../pa-prop-types';
import { Keys } from '../../../patient-types';
import BellyMaleButtons from './buttons/BellyMaleButtons';

function BodyPartSelectorBelly(props: BodySelectorProps) {
  const { standalone, question, onValueChange, answerValue: answerValueInit, locale } = props;

  const [, { t }] = useTranslations({ locale });

  const [answerValue, setAnswerValue] = useState(answerValueInit || {});
  const [requireNoteKey, setRequireNoteKey] = useState<string | null>(null);

  const mutExcAnswerKeys = getMutualExclusiveAnswerKeys({ question });
  const requireNoteKeys = getRequireNoteAnswerKeys({ question });
  const mainOptionKeys = getMainOptionAnswerKeys({ question });
  // const omitInSearchKeys = getOmitInSearchAnswerKeys({ question });

  const selectedKeys = answerValue.answerKeys || [];
  const selectedButtonKeys = [...selectedKeys];

  function handleClick({ id }: { id: string }) {
    // eslint-disable-next-line
    console.log('id', id);

    const selected = selectedKeys.includes(id);
    const isMutExcl = mutExcAnswerKeys.includes(id);

    setRequireNoteKey(null);

    let nKeys: Keys = [...selectedKeys];

    if (isMutExcl) {
      // ONLY INCLUDE MUT KEY
      if (selected) {
        nKeys = [];
      } else {
        nKeys = [id];
      }
    } else {
      // EXCLUDE ALL MUT KEYS
      nKeys = nKeys.filter((k) => !mutExcAnswerKeys.includes(k));
      // eslint-disable-next-line no-lonely-if
      if (selected) {
        nKeys = nKeys.filter((i) => i !== id);
      } else {
        nKeys = uniq([...nKeys, id]);
      }
    }

    const nValue = produce(answerValue, (dr) => {
      dr.answerKeys = nKeys;
    });

    setAnswerValue(nValue);
    onValueChange(nValue);
  }

  return (
    <div className='t_BodyPartSelectorBelly flex-col overflow-hidden'>
      <div className={classNames('flex-col overflow-auto', !standalone && 'p-4')}>
        {isNotEmpty(mainOptionKeys) && (
          <div className='flex gap-2 flex-wrap'>
            {mainOptionKeys?.map((mutKey) => {
              const isRequireNote = requireNoteKeys.includes(mutKey);
              const selected = selectedKeys.includes(mutKey);

              return (
                <button
                  key={mutKey}
                  className='flex-1 btn-base'
                  onClick={() => {
                    if (!selected) {
                      setRequireNoteKey(null);

                      if (isRequireNote) {
                        setRequireNoteKey(mutKey);
                        return;
                      }
                    }

                    handleClick({ id: mutKey });
                  }}
                >
                  {selected ? (
                    <BsPlusCircle size={22} className='text-primary rotate-45' />
                  ) : (
                    <BsFillPlusCircleFill size={22} className='text-primary' />
                  )}
                  <span className='text-base'>{upperCase(t(mutKey))}</span>
                </button>
              );
            })}
          </div>
        )}

        {requireNoteKey && (
          <div className='flex-none flex pt-3 gap-2'>
            <label className='mui-input flex-1'>
              <input
                placeholder=''
                value={answerValue?.notesForKeys?.[requireNoteKey] || ''}
                onChange={(e) => {
                  const nValue = produce(answerValue, (dr) => {
                    if (!dr.notesForKeys) dr.notesForKeys = {};
                    dr.notesForKeys[requireNoteKey] = e.target.value;
                  });

                  setAnswerValue(nValue);
                }}
              />
              <span>AREA DESCRIPTION</span>
            </label>

            <div className='flex-none'>
              <button
                className='btn btn-primary'
                onClick={() => {
                  if (!isNotEmpty(answerValue?.notesForKeys?.[requireNoteKey])) {
                    return;
                  }

                  handleClick({ id: requireNoteKey });
                }}
              >
                ADD
              </button>
            </div>
          </div>
        )}

        <div className='relative max-w-full pt-2'>
          <div className='pt-14 pb-6'>
            {/* BODY IMAGE */}
            <div className='z-10 relative flex-center'>
              <BellyMaleButtons
                selectedKeys={selectedButtonKeys}
                className='relative'
                onClick={(e) => {
                  const el = e.target as HTMLElement;
                  const id = el.getAttribute('id');
                  if (id) {
                    handleClick({ id });
                  }
                }}
              />
            </div>

            <div className='absolute flex-center top-0 w-full flex justify-between opacity-60'>
              <span className='p-5 text-sm'>RIGHT</span>
              <span className='p-5 text-sm'>LEFT</span>
            </div>

            {/* SELECT BUTTONS */}
            {/* <div className='absolute top-[5.4rem] w-full flex-center mx-0 px-0'> */}
            {/* </div> */}
          </div>
        </div>
      </div>

      {!isEmpty(selectedKeys) && (
        <div
          className={classNames('flex-none flex-col pt-4 gap-3 border-t', !standalone && 'px-4')}
        >
          <div className='flex gap-3 flex-wrap'>
            {selectedKeys.map((answerKey) => {
              const note = answerValue?.notesForKeys?.[answerKey];
              return (
                <button
                  key={answerKey}
                  className='btn btn-outline btn-primary border-primary p-2 min-h-min h-9 flex gap-2'
                  onClick={() => {
                    handleClick({ id: answerKey });
                  }}
                >
                  <span className='uppercase'>{t(answerKey)}</span>
                  {note ? ` (${note})` : null}
                  <MdClose size={16} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(BodyPartSelectorBelly);
